import React, { useState, useEffect, useRef, useCallback, Memo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./CarHistory.css"; // For styling the inputs, list, and map

const CarHistory = ({ setRefresh }) => {
  const [carNo, setCarNo] = useState("");
  const [date, setDate] = useState("");

  const [startHour, setStartHour] = useState("00");
  const [startMinute, setStartMinute] = useState("00");
  const [stopHour, setStopHour] = useState("23");
  const [stopMinute, setStopMinute] = useState("59");

  const [timestart, setTimestart] = useState(`${startHour}:${startMinute}:00`);
  const [timestop, setTimestop] = useState(`${stopHour}:${stopMinute}:00`);

  const [isFetching, setIsFetching] = useState(false);

  const [maxSpeed, setMaxSpeed] = useState(0);
  const [carHistoryData, setCarHistoryData] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null); // Selected log for focusing
  const [zoomLevel, setZoomLevel] = useState(17); // Zoom level
  const [shouldFocus, setShouldFocus] = useState(false); // Focus state for markers
  const mapRef = useRef(null); // Map reference to control zoom and bounds

  // State to hold max recorded speed and corresponding timestamp
  const [maxRecordedSpeed, setMaxRecordedSpeed] = useState(null);
  const [maxSpeedTime, setMaxSpeedTime] = useState(null);

  useEffect(() => {
    console.log("setRefresh(20000);  ");
    setRefresh(200000);
  });

  // Fetch car history data from the API
  const fetchCarHistory = async () => {
    setIsFetching(true);
    try {
      const response = await fetch(`https://www.arkadopoulos.eu/gpstracking/getCarHistory.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carNo, date, timestart, timestop }),
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setCarHistoryData(data);
        console.log(data);
        // Find the max speed and the corresponding gpstime
        const maxSpeedEntry = data.reduce((max, entry) => (entry.speed > max.speed ? entry : max), data[0]);
        setMaxRecordedSpeed(maxSpeedEntry.speed);
        setMaxSpeedTime(maxSpeedEntry.gpstime);
      } else {
        setCarHistoryData([]);
        console.error("Unexpected data format from API", data);
      }
    } catch (error) {
      console.error("Error fetching car history:", error);
      setCarHistoryData([]);
    } finally {
      setIsFetching(false); // Hide the popup
      fitBounds();
    }
  };

  // Handle row click for focusing on a specific log
  const handleRowClick = (log) => {
    setSelectedLog(log);
    setShouldFocus(true); // Trigger focus when a row is selected
  };

  // Fit bounds around all markers
  const FitBounds = ({ logs }) => {
    const map = useMapEvents({});
    const bounds = new L.LatLngBounds(logs.map((log) => [log.lat, log.lon]));

    useEffect(() => {
      if (shouldFocus && logs.length > 0 && map) {
        if (selectedLog) {
          map.flyTo([selectedLog.lat, selectedLog.lon], zoomLevel);
        } else {
          map.fitBounds(bounds); // Fit all points if no specific log is selected
        }
        setShouldFocus(false); // Reset the focus state after adjusting bounds
      }
    }, [bounds, logs.length, map]);

    return null;
  };

  const fitBounds = () => {
    if (mapRef.current && carHistoryData.length > 0) {
      const validData = carHistoryData.filter(({ lat, lon }) => lat && lon);
      if (validData.length > 0) {
        const bounds = L.latLngBounds(validData.map(({ lat, lon }) => [lat, lon]));
        mapRef.current.fitBounds(bounds);
      } else {
        console.warn("No valid data points to fit bounds.");
      }
    } else {
      console.warn("Map reference or car history data is not available.");
    }
  };

  const toggleFocusSelected = () => {
    if (mapRef.current && selectedLog) {
      mapRef.current.flyTo([selectedLog.lat, selectedLog.lon], zoomLevel);
    } else if (!selectedLog) {
      alert("Please select a log to focus on.");
    } else {
      console.error("Map instance is not available.");
    }
  };

  // Helper function to generate a range of numbers as strings
  const generateOptions = (start, end) => {
    const options = [];
    for (let i = start; i <= end; i++) {
      options.push(i.toString().padStart(2, "0"));
    }
    return options;
  };

  const hours = generateOptions(0, 23); // Generate hours from 00 to 23
  const minutes = generateOptions(0, 59); // Generate minutes from 00 to 59

  // Update timestart dynamically
  const handleTimestartChange = (type, value) => {
    const [hour, minute] = timestart.split(":");
    if (type === "hour") setTimestart(`${value}:${minute}:00`);
    if (type === "minute") setTimestart(`${hour}:${value}:00`);
  };

  const handleTimestopChange = (type, value) => {
    const [hour, minute] = timestop.split(":");
    if (type === "hour") setTimestop(`${value}:${minute}:00`);
    if (type === "minute") setTimestop(`${hour}:${value}:00`);
  };

  return (
    <div className="car-history-container">
      {isFetching && (
        <div className="fetching-popup">
          <p>Fetching data...</p>
        </div>
      )}

      {/* Display max speed and timestamp */}
      <div className="max-speed-info">
        {maxRecordedSpeed !== null ? (
          <p>
            Max recorded speed: {maxRecordedSpeed} km/h at {maxSpeedTime}
          </p>
        ) : (
          <p>No data available yet.</p>
        )}
      </div>

      <div className="input-container">
        <input type="text" placeholder="Car No" value={carNo} onChange={(e) => setCarNo(e.target.value)} />
        <input type="date" placeholder="Date" value={date} onChange={(e) => setDate(e.target.value)} />

        <div className="time-input">
          <label>Time Start:</label>
          <select
            value={startHour}
            onChange={(e) => {
              setStartHour(e.target.value);
              handleTimestartChange("hour", e.target.value);
            }}>
            {hours.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
          :
          <select
            value={startMinute}
            onChange={(e) => {
              setStartMinute(e.target.value);
              handleTimestartChange("minute", e.target.value);
            }}>
            {minutes.map((minute) => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>
        </div>
        <div className="time-input">
          <label>Time Stop:</label>
          <select
            value={stopHour}
            onChange={(e) => {
              setStopHour(e.target.value);
              handleTimestopChange("hour", e.target.value);
            }}>
            {hours.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
          :
          <select
            value={stopMinute}
            onChange={(e) => {
              setStopMinute(e.target.value);
              handleTimestopChange("minute", e.target.value);
            }}>
            {minutes.map((minute) => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>
        </div>

        <input type="number" placeholder="Max Speed" value={maxSpeed} onChange={(e) => setMaxSpeed(e.target.value)} />
        <button onClick={fetchCarHistory}>Fetch History</button>
      </div>

      <div className="content">
        {/* List of log entries */}
        <div className="scrollable-list">
          {Array.isArray(carHistoryData) && carHistoryData.length > 0 ? (
            carHistoryData.map((entry, index) => (
              <div key={index} className={`list-item ${selectedLog === entry ? "selected-row" : ""} ${entry.speed > maxSpeed ? "red-item" : ""}`} onClick={() => handleRowClick(entry)}>
                Speed: {entry.speed} km/h, Loc: {entry.lat}, {entry.lon}, Time: {entry.gpstime}
              </div>
            ))
          ) : (
            <div>No data available</div>
          )}
        </div>

        {/* Map and controls */}
        <div className="map-container">
          <MapContainer
            center={[39.61, 20.79]} // Initial map center
            zoom={zoomLevel}
            style={{ width: "100%", height: "100%" }}
            whenCreated={(mapInstance) => (mapRef.current = mapInstance)} // Store map instance in ref
          >
            <TileLayer url="https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiYXJrYWRvbmlrb3MiLCJhIjoiY20xczhyMGM2MDQ1ejJqc2p1YWM0cjBsbCJ9.J9xhMre3w3wK66hD8yqx9A" />
            {/* Plot markers for car history data */}
            {carHistoryData.map((log, index) => (
              <Marker
                key={index}
                position={[log.lat, log.lon]}
                icon={L.divIcon({
                  className: "custom-icon",
                  html: `
        <svg xmlns="http://www.w3.org/2000/svg" width="${selectedLog === log ? 20 : 10}" height="${selectedLog === log ? 20 : 10}">
          <circle 
            cx="${selectedLog === log ? 10 : 5}" 
            cy="${selectedLog === log ? 10 : 5}" 
            r="${selectedLog === log ? 10 : 5}" 
            fill="${log.speed > maxSpeed ? "red" : "rgba(0, 255, 0, 1)"}" 
          />
        </svg>
      `,
                  iconSize: [selectedLog === log ? 20 : 10, selectedLog === log ? 20 : 10], // Adjust icon size
                  iconAnchor: [selectedLog === log ? 10 : 5, selectedLog === log ? 10 : 5], // Center the circle
                })}>
                <Popup>
                  <div>
                    <p>
                      <strong>Speed:</strong> {log.speed} km/h
                    </p>
                    <p>
                      <strong>Lat:</strong> {log.lat}
                    </p>
                    <p>
                      <strong>Lon:</strong> {log.lon}
                    </p>
                    <p>
                      <strong>Time:</strong> {log.gpstime}
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
            <FitBounds logs={carHistoryData} />
          </MapContainer>

          {/* Controls for zoom and focusing */}
          <div className="controls">
            <button onClick={fitBounds}>Bound All Points</button>
            <button onClick={toggleFocusSelected}>Focus Selected</button>
            <label htmlFor="zoomLevel">Zoom Level: {zoomLevel}</label>
            <input type="range" id="zoomLevel" min="12" max="18" step="0.1" value={zoomLevel} onChange={(e) => setZoomLevel(parseFloat(e.target.value))} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarHistory;
